import { cloneDeep, isEmpty, set } from "lodash-es"
import { action, computed, makeObservable, observable, toJS } from "mobx"

import settingsApi from "@/api/settings"
import { CheckoutSetting_Checkout, transNewText } from "@/api/settings/type/emailConfig"
import { languageList } from "@/pages/Settings/EmailEdit/type"
import stores from "@/stores"
import { createUndoHistory } from "@/utils/createUndoHistory"
import { deepDiffBetweenObjects } from "@/utils/ObjectUtils"

type CheckoutEditableOptions = Pick<CheckoutSetting_Checkout, "translations" | "link_direction">

export default class SettingsPageStore {
  @observable isScriptWidget = false
  @observable checkoutInited = false
  @observable locale =  "en"

  /**
   * Checkout版本的Order Status widget 配置
   */
  @observable OrderStatusWidgetDataCheckout: CheckoutSetting_Checkout = {
    "primary_locale": "",
    "shop_locales": [],
    "link_direction": 0,
    "translations": {
      "en": {
        "title": "",
        "description": "",
        "track": "",
      },
    },
    "default_translations": {
      "en": {
        "title": "",
        "description": "",
        "track": "",
      },
    },
  }

  /**
   * Checkout widget 编辑的撤销 / 重做历史（General 页）
   */
  public history = createUndoHistory<CheckoutEditableOptions>({
    getSnapshot: () => {
      const { translations, link_direction } = this.OrderStatusWidgetDataCheckout

      return toJS({ translations, link_direction })
    },
    applySnapshot: (snapshot) => this.applyCheckoutSnapshot(snapshot),
  })

  constructor() {
    makeObservable(this)
  }

  @computed
  get optionsCheckout(): object {
    return cloneDeep(this.OrderStatusWidgetDataCheckout)
  }

  @action.bound
  getLanguageList(defaultText: string): { label: string, value: string }[] {
    const { shop_locales, primary_locale } = this.OrderStatusWidgetDataCheckout

    return shop_locales.map(locale => {
      const target = languageList.find(item => item.value === locale) as { name: string, value: string }

      return { label: primary_locale === locale ? `${target.name} (${defaultText})` : target.name, value: target.value }
    })
  }

  // 获取script的翻译文本
  @computed
  get translationTextsCheckout(): transNewText {
    const { translations } = this.OrderStatusWidgetDataCheckout

    return translations[this.locale || "en"] || {}
  }

  // 获取script的翻译文本
  @computed
  get defaultTranslationTextsCheckout(): transNewText {
    const { default_translations } = this.OrderStatusWidgetDataCheckout

    return default_translations[this.locale || "en"] || {}
  }

  @action.bound
  setOrderStatusWidgetDataCheckout(data: CheckoutSetting_Checkout, init?: boolean): void {
    this.OrderStatusWidgetDataCheckout = data
    const { translations, link_direction } = this.OrderStatusWidgetDataCheckout

    settingsApi.setCheckoutInitOptions({ translations, link_direction })
    this.history.reset()
    if (init) {
      this.locale = data.primary_locale
      this.checkoutInited = true
    }
  }

  @action.bound
  setWidgetSetting(path: string, data: any): void {
    // 限制长度为200
    if (typeof data === "string" &&  data.length >  200) {
      data = data.slice(0, 200)
    }
    set(this.OrderStatusWidgetDataCheckout, path, data)

    stores.commonStore.setContextualSaveBar("show", !isEmpty(this.getDiffOptions()))
    this.history.record()
  }

  @action.bound
  applyCheckoutSnapshot({ translations, link_direction }: CheckoutEditableOptions): void {
    this.OrderStatusWidgetDataCheckout.translations = translations
    this.OrderStatusWidgetDataCheckout.link_direction = link_direction

    stores.commonStore.setContextualSaveBar("show", !isEmpty(this.getDiffOptions()))
  }
  @action.bound
  setDefaultLinkDirection(path: string, data: any): void {
    set(this.OrderStatusWidgetDataCheckout, path, data)

    const { translations } = this.OrderStatusWidgetDataCheckout

    settingsApi.setCheckoutInitOptions({ translations, link_direction: data })
    this.history.reset()

    settingsApi.setTrackingCheckoutWidget({
      link_direction: data,
    }).then((res) => ({
      type: "widgetCheckout",
      data: res,
    }))
  }
  @action.bound
  changeLocale(locale: string): void {
    this.locale = locale
  }
  @action.bound
  setIsScriptWidget(type: boolean): void {
    this.isScriptWidget = type
  }

  @action.bound
  getDiffOptions(): boolean {
    const { translations, link_direction } = this.OrderStatusWidgetDataCheckout

    return deepDiffBetweenObjects({ translations, link_direction }, settingsApi.getCheckoutInitOptions())
  }
}
